import { AuthenticatedUserContext, InstitutionsByCodesRoute, OnlinePatientManagementContentProvider, OnlinePatientManagementContext, RouteLoading, useIsMobile } from "@ngt/opms";
import { useContext, useMemo } from "react";
import { UserType } from "./api/dtos";

import { faUpload } from '@fortawesome/pro-duotone-svg-icons';
import { faCopyright } from '@fortawesome/pro-regular-svg-icons/faCopyright';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from "@mui/material";
import { Permission, usePermissionsByIds } from "@ngt/opms-bctapi";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AppSettings } from "./AppSettings";
import ToggleFeedbackButton from "./components/buttons/ToggleFeedbackButton";
import useSentryUser from "./hooks/sentry/useSentryUser";
import ErrorPage, { ErrorTypeEnum } from "./pages/ErrorPage";
import ImageUploadPage from "./pages/ImageUpload/ImageUploadPage";
import MriImageUploadPage from "./pages/ImageUpload/MriImageUploadPage";

const App = () => {
    useSentryUser();
    
    const { data: user } = useContext(AuthenticatedUserContext);
    const { routeParameters } = useContext(OnlinePatientManagementContext);

    const isMobile = useIsMobile();

    const {
        permissions: [
            canAccessOpms, 
            canAdministerOpms,
            canCentralReview,
            isSiteUser
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsAccess,
            Permission.OpmsAdminister,
            Permission.CanCentralReview,
            Permission.IsSiteUser
        ]
    );

    const menuItems: any[] = useMemo(() => {
        if (!user || user.type === UserType.Patient) {
            return [];
        }

        let items: any[] = [];

        if (AppSettings.enableDicom && (canCentralReview || canAdministerOpms || isSiteUser)) {
            items.push(
                {
                    url: '/image-upload',
                    label: 'Image Upload',
                    external: false,
                    newTab: false,
                    icon: faUpload as any
                }
            );
        }

        return items;
    }, [canAdministerOpms, canCentralReview, isSiteUser, user]);

    const invalidPermission = user && !permissionsLoading && !canAccessOpms;
    
    const additionalHeaderComponents = useMemo(() => {
        return (
            AppSettings.sentry.dsn && 
            AppSettings.sentry.enableReportABug ?
                [<ToggleFeedbackButton />] :
                undefined
        );
    }, []);

    if (permissionsLoading && user) {
        return (
            <RouteLoading />
        );
    }

    if (invalidPermission) {
        return (
            <OnlinePatientManagementContentProvider
                menuItems={menuItems}
                footer={
                    isMobile ?
                        undefined :
                        <Typography variant="caption">
                            Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                        </Typography>
                }
                additionalHeaderComponents={additionalHeaderComponents}
            >
                <ErrorPage errorType={ErrorTypeEnum.e403} />
            </OnlinePatientManagementContentProvider>
        );
    }

    if (!user) { 
        return (
            <Routes>
                <Route 
                    path="/" 
                    element={
                        <OnlinePatientManagementContentProvider
                            menuItems={menuItems}
                            footer={
                                isMobile ?
                                    undefined :
                                    <Typography variant="caption">
                                        Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                                    </Typography>
                            }
                            additionalHeaderComponents={additionalHeaderComponents}
                        >
                            <Outlet />
                        </OnlinePatientManagementContentProvider>
                    }
                >
                    <Route path="*" element={<Navigate to="/" replace />} />            
                </Route>
            </Routes>
        )
    }

    return (
        <Routes>
            <Route 
                path="/" 
                element={
                    <OnlinePatientManagementContentProvider
                        menuItems={menuItems}
                        footer={
                            isMobile ?
                                undefined :
                                <Typography variant="caption">
                                    Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                                </Typography>
                        }
                        additionalHeaderComponents={additionalHeaderComponents}
                    >
                        <Outlet />
                    </OnlinePatientManagementContentProvider>
                }
            >
                {
                    AppSettings.enableDicom &&
                    <Route
                        path="image-upload"
                        element={
                            <InstitutionsByCodesRoute
                                loadBeforeMount
                            >
                                <Outlet />
                            </InstitutionsByCodesRoute>
                        }>
                        <Route index element={<ImageUploadPage />} />
                        <Route path="patient/:patientId" element={<MriImageUploadPage />} />
                        <Route path={`:${routeParameters.institutionCode}`}>
                            <Route index element={<ImageUploadPage />} />
                            <Route path="patient/:patientId" element={<MriImageUploadPage />} />
                        </Route>
                    </Route>
                }

                <Route path="error/403" element={<ErrorPage errorType={ErrorTypeEnum.e403} />} />
                <Route path="error/404" element={<ErrorPage errorType={ErrorTypeEnum.e404} />} />
                <Route path="error/405" element={<ErrorPage errorType={ErrorTypeEnum.e405} />} />
                <Route path="error/500" element={<ErrorPage errorType={ErrorTypeEnum.e500} />} />
                <Route index element={<Navigate to="/image-upload" replace />} />
                <Route path="*" element={<Navigate to="/image-upload" replace />} />
            </Route>
        </Routes>
    );
}

export default App
